// 動態 chunk 載入失敗的復原。依「本地快取是否真的壞掉」分成兩類處置，不可混用：
//
// A 類（內容不對）：`Invalid or unexpected token`（CDN 回傳損壞但 200 的 JS 被 Workbox 快取後永久
//   重現，CLIENT-PWA-SSR-BD）、`Unexpected end of input`（同樣是拿到損壞但 200 的 JS，差別只在損壞
//   位置落在檔尾，解析到 EOF 時括號／區塊仍未閉合；與 `Invalid or unexpected token` 同為 V8 對「本地
//   拿到的位元組壞掉」的兩種措辭，CLIENT-PWA-SSR-8Q 的 vue-error 事件即為此類）、
//   `does not provide an export named`（PWA 快取殘留舊 chunk，named export 與其他 chunk 對不上；
//   單一一致 build 內不可能發生，必為新舊 build 混用，
//   CLIENT-PWA-SSR-ZM）。壞的就是本地快取，必須清掉所有快取 + 反註冊 SW 才能復原。
//   註 1：只有「位元組已送達但解析失敗」才屬 A 類；若位元組根本沒送達，瀏覽器丟的是 B 類的 fetch 失敗措辭。
//   註 2：`JSON.parse('')` 的 `Unexpected end of JSON input` 不含本字串，不會被誤判為 chunk 損壞。
//   註 3：A 類的措辭必須逐一引擎補齊（B 類已列四種措辭）。`Unexpected end of script` 是 JavaScriptCore
//     對「解析到 EOF 時仍未閉合」的措辭，即 V8 `Unexpected end of input` 的 WebKit 對應版本
//     （CLIENT-PWA-SSR-1TB：iOS 第一方 async component chunk 被截斷，vue:error 有進到本檔但兩條 regex
//     皆未命中，iOS 因此完全拿不到復原，而同一情境的 Android 走 `Unexpected end of input` 會復原）。
//   註 4：JSC 另一個 EOF 措辭 `Unexpected EOF`（CLIENT-PWA-SSR-19D）仍不納入：JSC 的 JSON.parse 失敗訊息
//     為 `JSON Parse error: Unexpected EOF`，納入會讓一般 JSON 解析失敗被誤判成 chunk 損壞而清掉整包快取
//     （V8 側不存在此問題，故註 2 成立）。19D 屬另一個 issue，另案評估。
//
// B 類（取檔失敗）：四種引擎措辭的 module fetch 失敗（CLIENT-PWA-SSR-1A9 / H3）。2026-08-09 以 Sentry
//   資料驗證：事件的 gitSha 一律等於當時線上的 sha（pool/bundle.txt），且事件中的失敗 chunk URL
//   逐一實抓皆仍回 200 —— 檔案好好地在 CDN 上，本地快取沒有壞，屬行動網路／CDN 的偶發取檔失敗。
//   此時清快取只會讓使用者在同一條劣化連線上重抓整包 precache，是淨傷害，故僅重整不清快取。
//   仍需重整的原因：依 HTML 規範，取檔失敗的 module URL 會在該文件的 module map 內被標記為失敗，
//   同一份文件內後續 import() 只會立即回傳同一個 rejection，重試無效，只有換一份文件才能復原。
//
// 兩類各自持有獨立的 sessionStorage guard（每個 session 至多復原一次，避免無限重載循環）。
// guard 必須分開：B 類事件量約為 A 類的 66 倍（30 天 3,725 vs 56），共用一個 key 會讓 B 類幾乎
// 必然先把額度用掉，導致真正需要清快取的 A 類永遠等不到復原。
import { chunkErrorEvent } from '#build/nuxt.config.mjs'
import { usePwaUpdate } from '@/composables/core/usePwaUpdate'

const PURGE_GUARD_KEY = 'chunk-recovery-reloaded'
const RELOAD_GUARD_KEY = 'chunk-recovery-soft-reloaded'

const CORRUPT_CHUNK_RE = /Invalid or unexpected token|Unexpected end of input|Unexpected end of script|does not provide an export named/i
// app.cdnURL 指向另一個 origin，lazy chunk 一律是跨網域的 module script、恆以 CORS 模式取檔，故 CDN 少回
// Access-Control-Allow-Origin（錯誤回應、中間層快取到無標頭的版本）時，WebKit 走的是 CORS 拒絕而非一般
// 取檔失敗，措辭與其他三種都不同，少了這條 iOS 不會觸發復原重整、只有 Android 會（CLIENT-PWA-SSR-H3）。
// 兩種變體都必須保留 `script load` / `.js` 這類腳本語意，不可只取共通後綴 `denied by Cross-Origin Resource
// Sharing policy` —— 該句在 WebKit 是 DocumentThreadableLoader（fetch / XHR 共用）的措辭，跨域 API 轉址失敗
// 同樣會命中；而本檔任何命中都會觸發整頁重整，誤觸會讓使用者填到一半的表單全部遺失（第三方注入腳本的
// 未捕捉 fetch rejection 確實會冒泡到本檔的 unhandledrejection listener，見 howler.client.js 檔頭的前例）。
const CORS_DENIED_SCRIPT_RE =
  /Cross-origin script load denied by Cross-Origin Resource Sharing policy|Cross-origin redirection to \S+\.m?js\S* denied by Cross-Origin Resource Sharing policy/i
// 修改此清單時請同步 sentry.client.config.ts 的 CHUNK_IMPORT_ERROR_RE / CORS_DENIED_SCRIPT_RE
const FETCH_CHUNK_RE = new RegExp(
  [
    'Importing a module script failed',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    CORS_DENIED_SCRIPT_RE.source,
  ].join('|'),
  'i'
)

// Nuxt 4.2 內建的 chunkErrorEvent listener 直接讀 `event.payload.message`，而 Firefox 的 import() 可能以
// 非 Error 值 reject，Vite 原樣放進 payload，listener 便拋出未捕捉的 TypeError（CLIENT-PWA-SSR-15C，上游
// nuxt#35155 已改 optional chaining）。payload 不可替換成新 Error：nuxt:chunk-reload 以 identity 比對
// app:chunkError 收到的 error 與 router.onError 收到的 rejection（Vite 拋的是原值），替換會讓換頁重載失效。
// 故對這類事件代為觸發 hook 後截斷，其餘事件原樣放行。listener 必須在模組頂層註冊：同一 target 上的
// listener 依註冊順序執行（capture 也不例外，已以 Chromium 實測），而 Nuxt 在 createNuxtApp() 註冊自己的
// listener，只有 #build/plugins 被靜態 import 時求值的模組頂層早於它，plugin setup 已經太晚。
let chunkErrorNuxtApp = null
const handleMalformedChunkErrorEvent = (event) => {
  if (!chunkErrorNuxtApp || typeof event.payload?.message === 'string') return
  event.stopImmediatePropagation()
  chunkErrorNuxtApp.callHook('app:chunkError', { error: event.payload })
}
if (import.meta.client && chunkErrorEvent) {
  window.addEventListener(chunkErrorEvent, handleMalformedChunkErrorEvent)
}

export default defineNuxtPlugin((nuxtApp) => {
  chunkErrorNuxtApp = nuxtApp

  // A 類：清空所有快取 + 反註冊 SW + 重整
  const purgeAndReload = async (reason) => {
    if (sessionStorage.getItem(PURGE_GUARD_KEY)) return
    sessionStorage.setItem(PURGE_GUARD_KEY, '1')
    console.error('[chunk-recovery] purge caches + reload:', reason)
    try {
      const { forceReload } = usePwaUpdate()
      await forceReload()
    } catch (error) {
      console.error('[chunk-recovery] forceReload failed, hard reloading:', error)
      window.location.reload()
    }
  }

  // B 類：只重整，保留快取與 SW（precache 健在時重整幾乎瞬間完成）
  const reloadOnly = (reason) => {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
    console.warn('[chunk-recovery] reload without purging caches:', reason)
    window.location.reload()
  }

  const recover = (error, source) => {
    if (!(error instanceof Error)) return
    const message = error.message || ''
    if (CORRUPT_CHUNK_RE.test(message)) {
      purgeAndReload(`${source}: ${message}`)
      return
    }
    if (FETCH_CHUNK_RE.test(message)) {
      reloadOnly(`${source}: ${message}`)
    }
  }

  // Vue 非同步元件載入器（runtime-13）拋出的錯誤 → BD 與 1A9 的多數事件走這條
  nuxtApp.hook('vue:error', (error) => {
    recover(error, 'vue:error')
  })

  // Vite preload 失敗（多為 fetch 失敗），縱深補強
  nuxtApp.hook('app:chunkError', ({ error }) => {
    recover(error, 'app:chunkError')
  })

  // Vue render 邊界外的動態 import()（事件處理器 / composable 內）失敗時，其 rejection
  // 不會經過上述兩個 hook，改由全域 unhandledrejection 接住。
  // 不呼叫 event.preventDefault()，讓 rejection 仍正常上報以利 triage（guard 達上限後不再重載）。
  const handleUnhandledRejection = (event) => {
    recover(event?.reason, 'unhandledrejection')
  }
  window.addEventListener('unhandledrejection', handleUnhandledRejection)
  nuxtApp.hook('app:unmount', () => {
    window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    chunkErrorNuxtApp = null
  })
})
